import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { EMOJI_MAP, ACTIVE_COLORS } from './ReactionButton';
import type { ReactionType } from '../types';

const REACTION_TYPES: ReactionType[] = ['like', 'fire', 'heart', 'smooth'];

interface ReactionPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: ReactionType) => void;
  activeReactions: ReactionType[];
}

export function ReactionPicker({
  isOpen,
  onClose,
  onSelect,
  activeReactions,
}: ReactionPickerProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={ref}
          initial={{ opacity: 0, scale: 0.8, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="absolute bottom-full mb-2 start-0 flex items-center gap-1 rounded-full px-2 py-1.5 shadow-lg border border-white/30 z-30"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)', backdropFilter: 'blur(12px)' }}
        >
          {REACTION_TYPES.map((type) => {
            const isActive = activeReactions.includes(type);
            return (
              <button
                key={type}
                onClick={() => {
                  onSelect(type);
                  onClose();
                }}
                aria-label={t(`feed.reaction_${type}`)}
                className="w-9 h-9 rounded-full flex items-center justify-center text-lg transition-transform hover:scale-125 active:scale-90"
                style={{
                  boxShadow: isActive
                    ? `0 0 0 2px ${ACTIVE_COLORS[type]}`
                    : 'none',
                }}
              >
                {EMOJI_MAP[type]}
              </button>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
